import { Box, Typography } from '@mui/material';
import { makeStyles, ClassNameMap } from '@mui/styles';
import Actionbuttons from 'components/common/actions-buttons';
import TableComponent from 'components/Table';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, RendererParams } from 'types/common';
import { formatMoney } from 'utils/helpers';
import { AccountingInvolvedParty } from 'v2-types/order';

export type ColumnsType = { [key: string]: Column };

const useStyles = makeStyles((theme: any) => ({
  drawLine: {
    paddingLeft: theme.spacing(7),
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: theme.spacing(2),
      bottom: '50%',
      width: theme.spacing(5),
      border: `1px solid ${theme.palette.border.line}`,
      borderTop: '0 none transparent',
      borderRight: '0 none transparent'
    },
  },
  voidlist: {
    fontWeight: 500,
    color: theme.palette.tab.offselected,
    marginLeft: theme.spacing(1.5)
  },
  text: {
    color: theme.palette.primary.darker,
    fontSize: 14,
    width: 'max-content',
    textTransform: 'capitalize',
  },
}));

const partiesColumns = (
  handleEdit: (row: any) => void,
  handleDelete: (row) => void,
  classes: ClassNameMap<"drawLine" | "text" | "voidlist">
): ColumnsType => ({
  actions: {
    header: {
      title: '',
      minWidth: '80px',
      width: '80px'
    },
    renderer: ({ row }: RendererParams) => (
      <Actionbuttons
        handleEdit={(e) => {
          e.stopPropagation();
          handleEdit(row);
        }}
        handleDelete={(e) => {
          e.stopPropagation();
          handleDelete(row);
        }}
      />
    ),
  },
  name: {
    header: {
      title: 'name',
      align: 'left'
    },
    renderer: ({ row }: RendererParams) => (
      <Typography className={classes.text}>{row?.name}</Typography>
    ),
  },
  kind: {
    header: {
      title: 'kind',
      align: 'left'
    },
    renderer: ({ row }: RendererParams) => (
      <Typography className={classes.text}>{row?.kind}</Typography>
    ),
  },
  amount: {
    header: {
      title: 'amount',
      align: 'left'
    },
    renderer: ({ row }: RendererParams) => (
      <Typography className={classes.text}>{formatMoney(row.amount)}</Typography>
    ),
  },
});

type involvedPartiesProps = {
  ledgerRow: any;
  handleEditLedgerAssociation: (row: any) => void,
  handleDeleteLedgerAssociation: (row: any) => void,
}

export const InvolvedPartiesTable: FC<involvedPartiesProps> = ({
  ledgerRow,
  handleEditLedgerAssociation,
  handleDeleteLedgerAssociation
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const parties: AccountingInvolvedParty[] = ledgerRow?.involved_parties || [];
  const handleEditPartyAssociation = (row: any) => {
    handleEditLedgerAssociation({ row, ledgerInfo: ledgerRow });
  };

  const handleDeletePartyAssociation = (row: any) => {
    handleDeleteLedgerAssociation({ row, ledgerInfo: ledgerRow });
  };

  return (
    <Box className={classes.drawLine}>
      <div style={{ padding: "16px 0px" }}>
        {parties.length > 0
          ? (
            <TableComponent
              columns={partiesColumns(handleEditPartyAssociation, handleDeletePartyAssociation, classes)}
              disabledBorderLeft
              dataSet={parties}
              cellAlignment="left"
            />
          )
          : (
            <Typography variant="body2" className={classes.voidlist}>
              {t('contact:no-items')}
            </Typography>
          )}
      </div>
    </Box>
  );
};
